import { useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

/**
 * How the sites on a round get decided: by kind, or by name.
 *
 * These are two ways of answering one question, and all three forms used to
 * show both at once — a category picker, a favor control, a place search and
 * a list of chosen places, stacked. Read top to bottom that looks like four
 * things to fill in, when in practice an operator is doing one of two things:
 * naming the kinds and letting the planner choose, or naming the places.
 */
export type SitesMode = 'kinds' | 'named';

/**
 * Which mode a form should open in, from what it already holds.
 *
 * Named only when there are places and no kinds: anything else — including an
 * empty form — starts on kinds, which is the mode that plans a day from
 * nothing. Read once, as initial state, so it never switches underneath
 * somebody while they are editing.
 */
export function initialSitesMode(
  interestKeys: readonly string[],
  chosenCount: number,
): SitesMode {
  return interestKeys.length === 0 && chosenCount > 0 ? 'named' : 'kinds';
}

/** The same hook in each form, so the three cannot drift apart. */
export function useSitesMode(
  interestKeys: readonly string[],
  chosenCount: number,
): [SitesMode, (next: SitesMode) => void] {
  const [mode, setMode] = useState<SitesMode>(
    () => initialSitesMode(interestKeys, chosenCount),
  );

  return [mode, setMode];
}

/**
 * The switch itself, with the line that explains what the other side keeps.
 *
 * Not a tab strip: there is no second panel behind it, only a smaller form.
 * Two buttons say plainly that these are alternatives, which the stacked
 * controls never did.
 */
export function SitesModeSwitch({
  mode, onChange, scope = 'day', variant = 'sites',
}: {
  mode: SitesMode;
  onChange: (next: SitesMode) => void;
  /** Whose sites these are, so the caption reads right on each form. */
  scope?: 'day' | 'operation';
  /**
   * What is being decided. A rest stop asks the same question about one
   * place rather than about a list, so it says "it" and not "them".
   *
   * Kept as a variant rather than as caption props, so every wording of this
   * choice lives in one file and the two read alike.
   */
  variant?: 'sites' | 'break';
}) {
  // "for the day" on one day; "for each day" where every day gets its own.
  const subject = scope === 'operation' ? 'each day' : 'the day';
  const isBreak = variant === 'break';

  return (
    <>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, next: SitesMode | null) => next && onChange(next)}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="kinds" sx={{ textTransform: 'none', px: 1.5 }}>
          Choose for me
        </ToggleButton>
        <ToggleButton value="named" sx={{ textTransform: 'none', px: 1.5 }}>
          {isBreak ? 'I’ll name it' : 'I’ll name them'}
        </ToggleButton>
      </ToggleButtonGroup>

      {/*
        The switch changes what is SENT, not only what is shown: the kinds
        come off the request in the named mode. Nothing on screen says so any
        more — the sentence that did was noise to everybody who had chosen no
        kinds — so the values are simply kept, and reappear with the mode.
      */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {isBreak
          ? (mode === 'kinds'
            ? 'The planner finds somewhere of the right kind near where you will be '
              + 'at that time.'
            : 'Choose the place yourself and the planner fits it into the round at '
              + 'that time.')
          : (mode === 'kinds'
            ? 'Select the types of sites you’re interested in, and the planner will '
              + 'automatically choose the sites and determine how many can fit within the '
              + `available time for ${subject}.`
            : 'Add the specific sites you want to visit. The planner includes every one '
              + 'of them and adds nothing of its own.')}
      </Typography>
    </>
  );
}
